import { motion } from "framer-motion";
import { BookOpen, Coffee, Heart, MoonStar } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    number: "01",
    icon: Coffee,
    heading: "Begin before the noise",
    tagline: "A quiet page before the first screen of the day.",
    description:
      "Sometimes the happiness we seek is already inside of us. This journal gives you a gentle place to find it — a few honest lines each morning about what you're thankful for, before the world asks anything of you.",
    badge: "Morning Practice",
  },
  {
    number: "02",
    icon: BookOpen,
    heading: "120 pages of your own story",
    tagline: "Room to notice, season after season.",
    description:
      "The more you look for moments to be thankful for, the more of them you see. With 120 pages in a comfortable 6 × 9 paperback, there is space for months of small, golden observations.",
    badge: "120 Pages",
  },
  {
    number: "03",
    icon: Heart,
    heading: "Your pace, your practice",
    tagline: "No streaks. No guilt. Just gratitude.",
    description:
      "Keeping a gratitude journal is one of the easiest ways to capture the good in everyday life. Write daily or wander back when you can — the pages wait patiently, and so does the joy.",
    badge: "Gentle Rhythm",
  },
  {
    number: "04",
    icon: MoonStar,
    heading: "A gift that gives back",
    tagline: "For anyone counting their blessings — or learning to.",
    description:
      "It makes a wonderful gift for someone who could use a little more light: a friend in a hard season, a graduate, a parent, or yourself. Gratitude shared has a way of multiplying.",
    badge: "A Thoughtful Gift",
  },
];

export const Manifesto = () => (
  <section id="about" data-testid="manifesto-section" className="relative py-24 sm:py-32 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl sm:mb-20"
      >
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
          The Journal
        </p>
        <h2 className="font-serif text-3xl font-medium leading-[1.1] tracking-tight text-espresso sm:text-5xl">
          Built for intention,
          <br />
          <em className="italic text-sienna">made for slow mornings.</em>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-walnut sm:text-lg">
          Four quiet promises that turn daily journaling from a chore into
          your favorite ritual.
        </p>
      </motion.div>

      <div className="border-t border-linenborder">
        {CHAPTERS.map((c, i) => (
          <motion.article
            key={c.number}
            data-testid={`manifesto-chapter-${c.number}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: i * 0.08, ease: EASE }}
            className="group grid gap-6 border-b border-linenborder py-10 transition-colors duration-500 hover:bg-paper/70 sm:py-12 lg:grid-cols-[110px_1fr_1.2fr_140px] lg:items-center lg:gap-10 lg:px-6"
          >
            <span className="font-serif text-5xl font-light text-terracotta/35 transition-colors duration-500 group-hover:text-terracotta sm:text-6xl">
              {c.number}
            </span>
            <div>
              <h3 className="font-serif text-2xl font-medium leading-snug text-espresso sm:text-3xl">
                {c.heading}
              </h3>
              <p className="mt-2 font-serif text-base italic text-terracotta sm:text-lg">
                {c.tagline}
              </p>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-walnut/90 sm:text-base">
              {c.description}
            </p>
            <div className="flex lg:justify-end">
              <span className="inline-flex items-center gap-2 rounded-full border border-linenborder bg-cream px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-sienna">
                <c.icon size={13} className="text-terracotta" />
                {c.badge}
              </span>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
